import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
} from "@material-ui/core";

import { useRuleStyles } from "../Rules/styles";
import { STATUS_OPTIONS } from "./venueListQuery";

const PINNED_COUNTRY = "US";

/**
 * Filter bar of the Venues list: the exact-match filters (country, state, status) and the two
 * "missing" checkboxes. They apply at once. The per-column text searches live in the table
 * header (VenuesTable), under the column they search.
 */
const VenueFilters = ({
  state,
  patch,
  countries,
  states,
  statesLoading,
  canClear,
  onClear,
}) => {
  const classes = useRuleStyles();

  // Only countries venues are in (from filter-options); United States first.
  const sortedCountries = useMemo(() => {
    const list = [...(countries || [])];
    const us = list.findIndex((c) => c.code === PINNED_COUNTRY);
    if (us > 0) list.unshift(list.splice(us, 1)[0]);
    return list;
  }, [countries]);

  const stateHelper = !state.country
    ? "Pick a country first"
    : !statesLoading && !(states || []).length
    ? "No states listed for this country"
    : " ";

  return (
    <Paper className={classes.filters} component="section" aria-label="Filters">
      <TextField
        id="venue-filter-country"
        select
        label="Country"
        variant="outlined"
        size="small"
        className={classes.filterField}
        value={state.country}
        onChange={(e) => patch({ country: e.target.value, state: "" })}
      >
        <MenuItem value="">All</MenuItem>
        {state.country &&
          !sortedCountries.some((c) => c.code === state.country) && (
            <MenuItem value={state.country}>{state.country}</MenuItem>
          )}
        {sortedCountries.map((c) => (
          <MenuItem key={c.code} value={c.code}>
            {c.name} ({c.count})
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id="venue-filter-state"
        select
        label="State"
        variant="outlined"
        size="small"
        className={classes.filterFieldNarrow}
        value={state.state}
        disabled={!state.country || (!statesLoading && !(states || []).length)}
        helperText={stateHelper}
        onChange={(e) => patch({ state: e.target.value })}
      >
        <MenuItem value="">All</MenuItem>
        {state.state &&
          !(states || []).some((s) => s.abbreviation === state.state) && (
            <MenuItem value={state.state}>{state.state}</MenuItem>
          )}
        {(states || []).map((s) => (
          <MenuItem key={s.abbreviation} value={s.abbreviation}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id="venue-filter-status"
        select
        label="Status"
        variant="outlined"
        size="small"
        className={classes.filterFieldNarrow}
        value={state.status}
        onChange={(e) => patch({ status: e.target.value })}
      >
        {STATUS_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={!!state.missingTimezone}
            onChange={(e) => patch({ missingTimezone: e.target.checked })}
          />
        }
        label="Missing timezone"
      />
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={!!state.missingMapLink}
            onChange={(e) => patch({ missingMapLink: e.target.checked })}
          />
        }
        label="Missing map link"
      />
      <Button
        variant="text"
        color="primary"
        className={classes.touch}
        disabled={!canClear}
        onClick={onClear}
      >
        Clear filters
      </Button>
    </Paper>
  );
};

VenueFilters.propTypes = {
  state: PropTypes.object.isRequired,
  patch: PropTypes.func.isRequired,
  countries: PropTypes.array,
  states: PropTypes.array,
  statesLoading: PropTypes.bool,
  canClear: PropTypes.bool,
  onClear: PropTypes.func.isRequired,
};

export default VenueFilters;
